import {
  CalendarIcon,
  CheckCircleIcon,
  ChevronLeftIcon,
  ChevronRightIcon,
  EnvelopeIcon,
  QuestionMarkCircleIcon,
  UserIcon,
  XCircleIcon,
} from "@heroicons/react/20/solid";
import moment from "moment";
import React, { useCallback, useEffect, useRef, useState } from "react";
import { withTranslation } from "react-i18next";
import { useSearchParams } from "react-router-dom";

import { useUser } from "../../user/utils/user";

import { get, post, unwrapError } from "../../common/utils/api";
import Map from "../components/Map";
import Palette from "../components/Palette";
import {
  addSentinel2Tiles,
  categories,
  colorToIndex,
  setSentinel2Visibility,
} from "../utils/map";

function DelineationReviewSingle({ showAlert, t }) {
  const mapCategory = useRef(null);
  const categoryRef = useRef(undefined);

  const [mapLoaded, setMapLoaded] = useState(false);

  const { user } = useUser();
  const canReview = user.user.is_supervisor;

  let [task, setTask] = useState({});
  const parcels = task.landcover2_submission_set || [];

  let [current, setCurrent] = useState(0);
  const currentData = parcels[current];

  function getApproval(d) {
    if (canReview) {
      return d.approval.approve;
    } else {
      if (d.approval_set.length === 0) {
        return null;
      } else {
        return d.approval_set.every((a) => a.approve);
      }
    }
  }

  const numApproved = parcels.reduce(
    (a, b) => a + (getApproval(b) === true),
    0,
  );
  const numRejected = parcels.reduce(
    (a, b) => a + (getApproval(b) === false),
    0,
  );

  const [searchParams] = useSearchParams();
  const taskSelected = searchParams.get("task_id");

  let [showParcels, setShowParcels] = useState(true);
  const [sceneVisible, setSceneVisible] = useState(true);

  const [autoNext, setAutoNext] = useState(true);

  const [isSubmitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (!mapLoaded) return;
    if (mapCategory.current.getLayer("parcels")) {
      mapCategory.current.setLayoutProperty(
        "parcels",
        "visibility",
        showParcels ? "visible" : "none",
      );
    }
    if (mapCategory.current.getLayer("parcelsOutline")) {
      mapCategory.current.setLayoutProperty(
        "parcelsOutline",
        "visibility",
        showParcels ? "visible" : "none",
      );
    }
    if (mapCategory.current.getLayer("parcelsApprove")) {
      mapCategory.current.setLayoutProperty(
        "parcelsApprove",
        "visibility",
        showParcels ? "visible" : "none",
      );
    }
    if (mapCategory.current.getLayer("parcelsReject")) {
      mapCategory.current.setLayoutProperty(
        "parcelsReject",
        "visibility",
        showParcels ? "visible" : "none",
      );
    }
  }, [showParcels, mapLoaded]);

  useEffect(() => {
    if (!mapLoaded) return;
    if (task.latitude === undefined) return;
    parcels.forEach((_, i) => {
      mapCategory.current.setFeatureState(
        { source: "parcels", id: i },
        { active: i == current },
      );
    });
  }, [current, mapLoaded]);

  useEffect(() => {
    if (!mapLoaded) return;
    if (task.latitude === undefined) return;
    parcels.forEach((p, i) => {
      const a = getApproval(p);
      mapCategory.current.setFeatureState(
        { source: "parcels", id: i },
        { approved: a === true },
      );
      mapCategory.current.setFeatureState(
        { source: "parcels", id: i },
        { rejected: a === false },
      );
    });
    if (task.scene_id) {
      addSentinel2Tiles(mapCategory.current, task.scene_id);
    }
  }, [task, mapLoaded]);

  useEffect(() => {
    if (mapLoaded) {
      setSentinel2Visibility(mapCategory.current, sceneVisible);
    }
  }, [sceneVisible, mapLoaded]);

  const getData = useCallback(async () => {
    get(`/landcover2/review/single/?task_id=${taskSelected}`).then(
      (response) => {
        const landcover2_submission_set =
          response.data.landcover2_submission_set.map((d) => {
            let label = d.data.properties.label;
            d.data.properties.portColor = categories.find(
              (c) => c.name === label,
            )?.color;
            let user_approval = { id: null };
            d.approval_set.forEach((a) => {
              if (a.approver === user.user.email) {
                user_approval = a;
              }
            });

            return {
              id: d.id,
              approval_set: d.approval_set,
              approval: user_approval,
              data: d.data,
            };
          });
        task = { ...response.data, landcover2_submission_set };
        setTask(task);
        setCurrentData(0, task);
      },
    );
  }, [taskSelected, user]);

  useEffect(() => {
    getData();
  }, [getData]);

  const handleAutoNextCheckbox = () => {
    setAutoNext(!autoNext);
  };

  const isEnd = current === parcels.length - 1;
  function previous() {
    if (current !== 0) {
      setCurrentData(current - 1);
    }
  }
  function next() {
    if (!isEnd) {
      setCurrentData(current + 1);
    }
  }

  function setCurrentData(index, t) {
    const _task = t || task;
    const _parcels = _task.landcover2_submission_set;
    if (_parcels.length === 0) {
      return;
    }
    const parcel = _parcels[index];
    categoryRef.current = colorToIndex[parcel.data.properties.portColor];
    setCurrent(index);
  }
  const updateApproval = (idx, approve) => {
    postApprovals([{ idx, approve }]);

    if (autoNext) {
      next();
    }
  };
  const approve = (idx) => updateApproval(idx, true);
  const reject = (idx) => updateApproval(idx, false);

  const updateAll = (approve) =>
    postApprovals(
      parcels.map((_, idx) => {
        return { idx, approve };
      }),
    );
  const approveAll = () => updateAll(true);
  const rejectAll = () => updateAll(false);

  function postApprovals(newApprovals) {
    setSubmitting(true);

    const toPost = newApprovals.map((a) => {
      return {
        id: parcels[a.idx].approval.id,
        submission: parcels[a.idx].id,
        approve: a.approve,
      };
    });
    post("/landcover2/approve/", toPost)
      .then(function (response) {
        setTask((t) => {
          const p = t.landcover2_submission_set;
          response.data.forEach((r, i) => {
            p[newApprovals[i].idx].approval = r;
            const new_approval_set = p[newApprovals[i].idx].approval_set.map(
              (s) => {
                if (s.approver === user.user.email) {
                  s.approve = r.approve;
                }
                return s;
              },
            );
            p[newApprovals[i].idx].approval_set = new_approval_set;
          });
          return { ...t, submission: p };
        });
        showAlert({
          type: "success",
          message: t("common.successfullySubmitted", "Successfully submitted"),
        });
      })
      .catch(function (error) {
        showAlert({ type: "error", message: unwrapError(error) });
      })
      .finally(() => setSubmitting(false));
  }

  return user ? (
    <React.Fragment>
      <div className="container mx-auto px-4 lg:px-8 py-2 my-2 ">
        <div className="border-b border-gray-200 bg-white px-4 py-5 sm:px-6 sm:rounded-lg shadow">
          <div className="-ml-4 -mt-2 md:flex md:items-center md:justify-between md:flex-row sm:flex-nowrap flex flex-col space-x-2 items-center">
            {/*User block*/}
            <div className="ml-4 mt-2 space-y-2">
              <div className="pb-2">
                <h1 className="text-2xl font-bold text-gray-900">
                  Land Cover Submission
                </h1>
              </div>
              <div className="flex">
                <CalendarIcon
                  className="mr-1.5 h-5 w-5 flex-shrink-0 text-gray-400"
                  aria-hidden="true"
                />
                <span className="text-sm text-gray-500">
                  {new Date(task.created_at).toLocaleString()}
                </span>
              </div>
              <div className="flex">
                <UserIcon
                  className="mr-1.5 h-5 w-5 flex-shrink-0 text-gray-400"
                  aria-hidden="true"
                />
                <span className="text-sm text-gray-500">
                  {task.user?.first_name} {task.user?.last_name}
                </span>
              </div>
              <div className="flex">
                <EnvelopeIcon
                  className="mr-1.5 h-5 w-5 flex-shrink-0 text-gray-400"
                  aria-hidden="true"
                />
                <span className="text-sm text-gray-500">
                  {task.user?.email}
                </span>
              </div>
            </div>
            {/*Approval block*/}
            <div className="mt-4 flex flex-col items-center justify-end">
              {/*Statuses */}
              <div className="flex flex-row space-x-4 justify-center">
                <div className="flex">
                  <CheckCircleIcon
                    className="mr-1.5 h-5 w-5 flex-shrink-0 text-green-500"
                    aria-hidden="true"
                  />
                  <span className="text-sm text-green-500">{numApproved}</span>
                </div>
                <div className="flex">
                  <XCircleIcon
                    className="mr-1.5 h-5 w-5 flex-shrink-0 text-red-500"
                    aria-hidden="true"
                  />
                  <span className="text-sm text-red-500">{numRejected}</span>
                </div>
                <div className="flex">
                  <QuestionMarkCircleIcon
                    className="mr-1.5 h-5 w-5 flex-shrink-0 text-gray-500"
                    aria-hidden="true"
                  />
                  <span className="text-sm text-gray-500">
                    {parcels.length - numApproved - numRejected}
                  </span>
                </div>
              </div>
              {/* Buttons*/}
              {canReview && (
                <div className="justify-stretch mt-4 flex flex-nowrap flex-row space-x-4 md:flex-col md:space-x-0 md:space-y-2">
                  <button
                    type="button"
                    onClick={approveAll}
                    disabled={isSubmitting}
                    className="inline-flex items-center justify-center rounded-md border px-4 py-2 text-sm font-medium bg-white hover:bg-gray-50 text-gray-700 border border-green-500 disabled:opacity-50"
                  >
                    <CheckCircleIcon
                      className="mr-1.5 h-5 w-5 flex-shrink-0 text-green-500"
                      aria-hidden="true"
                    />
                    <span className="text-green-800">Approve all</span>
                  </button>
                  <button
                    type="button"
                    onClick={rejectAll}
                    disabled={isSubmitting}
                    className="relative inline-flex items-center justify-center rounded-md border px-4 py-2 text-sm font-medium bg-white hover:bg-gray-50 text-gray-700 border border-red-500 disabled:opacity-50"
                  >
                    <XCircleIcon
                      className="mr-1.5 h-5 w-5 flex-shrink-0 text-red-500"
                      aria-hidden="true"
                    />
                    <span className="text-red-800">Reject all</span>
                  </button>
                </div>
              )}
            </div>
          </div>
          <div className="relative mt-4 flex flex-row justify-center gap-x-2">
            <Map
              map={mapCategory}
              interactive={false}
              showApproval={false}
              task={task}
              categoryRef={categoryRef}
              parcels={parcels}
              setCurrent={setCurrentData}
              mapLoaded={mapLoaded}
              setMapLoaded={setMapLoaded}
            >
              <div
                className="w-full text-center"
                style={{
                  position: "absolute",
                  top: 0,
                  zIndex: 9,
                }}
              >
                <button
                  onClick={() => setShowParcels(!showParcels)}
                  className="mt-2 inline-flex justify-center rounded-md border border-transparent bg-cyan-600 px-4 py-2 text-sm font-medium text-white shadow-sm hover:bg-cyan-700 focus:outline-none focus:ring-2 focus:ring-cyan-500 focus:ring-offset-2 disabled:bg-gray-500 disabled:cursor-not-allowed"
                >
                  {showParcels
                    ? t("common.hideParcels", "Hide Parcels")
                    : t("common.showParcels", "Show Parcels")}
                </button>
              </div>
            </Map>
            <div className="mt-0 ml-6 flex-shrink-0 flex-grow-0">
              <Palette categoryRef={categoryRef} interactive={false} />
              <div className="mt-6 text-sm text-gray-700">
                <div>
                  <span className="font-medium">Satellite date:</span>{" "}
                  {moment(task.scene_datetime).format("YYYY-MM-DD")}
                </div>
                <div>
                  Show satellite:
                  <input
                    id="comments"
                    aria-describedby="comments-description"
                    name="comments"
                    type="checkbox"
                    checked={sceneVisible}
                    onChange={() => setSceneVisible((visible) => !visible)}
                    className="h-4 w-4 ml-2 rounded border-gray-300 text-cyan-600 focus:ring-cyan-500"
                  />
                </div>
              </div>
            </div>
          </div>
        </div>
        {currentData && (
          <React.Fragment>
            <div className="mt-4 flex flex-wrap items-center justify-between px-4 sm:px-0">
              <div className="-mt-px flex w-0 flex-1"></div>
              <nav
                className="isolate inline-flex space-x-2 sm:space-x-4 rounded-md"
                aria-label="Pagination"
              >
                <div>
                  <button
                    type="button"
                    onClick={previous}
                    disabled={current === 0 || isSubmitting}
                    className="relative inline-flex items-center rounded-md border border-gray-300 bg-white px-2 py-2 text-sm font-medium text-gray-500 hover:bg-gray-50 focus:z-20 disabled:opacity-50"
                  >
                    <span className="sr-only">Previous</span>

                    <ChevronLeftIcon className="h-5 w-5" aria-hidden="true" />
                  </button>
                </div>
                <div className="flex justify-center">
                  <div
                    className={`relative inline-flex items-center border border-gray-300 rounded-l-md bg-white px-4 py-2 text-sm font-medium text-gray-500 focus:z-20 ${
                      canReview || "rounded-r-md"
                    } ${isSubmitting && "opacity-50"}`}
                  >
                    <span className="sm:hidden">#</span>
                    <span className="hidden sm:inline-block">Record</span>
                    &nbsp;
                    {current + 1} / {parcels.length}
                  </div>
                  {canReview && (
                    <React.Fragment>
                      <button
                        type="button"
                        onClick={() => approve(current)}
                        disabled={isSubmitting}
                        className={`relative inline-flex items-center justify-center border px-4 py-2 text-sm font-medium disabled:opacity-50 ${
                          getApproval(currentData) === true
                            ? "bg-green-100 hover:bg-green-200 border-green-500 "
                            : "bg-white hover:bg-gray-50 text-gray-700 border border-gray-300"
                        }`}
                      >
                        <CheckCircleIcon
                          className="h-5 w-5 flex-shrink-0 text-green-500"
                          aria-hidden="true"
                        />
                      </button>
                      <button
                        type="button"
                        onClick={() => reject(current)}
                        disabled={isSubmitting}
                        className={`relative inline-flex items-center justify-center border rounded-r-md px-4 py-2 text-sm font-medium disabled:opacity-50 ${
                          getApproval(currentData) === false
                            ? "bg-red-100 hover:bg-red-200 border-red-500 "
                            : "bg-white hover:bg-gray-50 text-gray-700 border border-gray-300"
                        }`}
                      >
                        <XCircleIcon
                          className="h-5 w-5 flex-shrink-0 text-red-500"
                          aria-hidden="true"
                        />
                      </button>
                    </React.Fragment>
                  )}
                </div>
                <div>
                  <button
                    type="button"
                    onClick={next}
                    disabled={isEnd || isSubmitting}
                    className="relative inline-flex items-center rounded-md border border-gray-300 bg-white px-2 py-2 text-sm font-medium text-gray-500 hover:bg-gray-50 focus:z-20 disabled:opacity-50"
                  >
                    <span className="sr-only">Next</span>
                    <ChevronRightIcon className="h-5 w-5" aria-hidden="true" />
                  </button>
                </div>
              </nav>
              <div className="-mt-px flex flex-1 justify-end">
                <div className="flex h-5 items-center">
                  <input
                    id="comments"
                    aria-describedby="comments-description"
                    name="comments"
                    type="checkbox"
                    checked={autoNext}
                    onChange={handleAutoNextCheckbox}
                    className="h-4 w-4 rounded border-gray-300 text-cyan-600 focus:ring-cyan-500"
                  />
                </div>
                <div className="ml-1 text-xs">
                  <label
                    htmlFor="comments"
                    className="font-medium text-gray-700"
                  >
                    Auto-next
                  </label>
                </div>
              </div>
            </div>
          </React.Fragment>
        )}
      </div>
    </React.Fragment>
  ) : null;
}
export default withTranslation()(DelineationReviewSingle);
